import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  FxAlert,
  FxButton,
  FxIcon,
  FxPanel,
  FxSegmentedTabs,
  FxSpinner,
  FxViewToggle,
} from '@/components/fx'
import { PageTitle, useShellHelp, useShellSearch } from '@/layouts/shellChrome'
import type { Template } from '@/types/domain'

import { DeleteFolderDialog } from './DeleteFolderDialog'
import { FolderNameDialog } from './FolderNameDialog'
import { MoveTemplateDialog } from './MoveTemplateDialog'
import { ProjectGrid } from './ProjectGrid'
import { ProjectTree } from './ProjectTree'
import { ProjektDialog } from './ProjektDialog'
import { ShareFolderDialog } from './ShareFolderDialog'
import { UnfiledPanel } from './UnfiledPanel'
import { useFavorites } from '@/lib/favorites'
import {
  applyTab,
  buildProjects,
  filterProjects,
  filterTemplates,
  sortByRecent,
  type ProjectNode,
  type ProjectTab,
} from './projectData'
import { TEMPLATE_PAGE_SIZE, useFolders, useProjectMutations, useTemplates } from './useProjects'

/**
 * Projekte — the projects (the gateway's *folders*) and the forms filed in
 * them.
 *
 * Three things on the artboard the backend cannot carry, each handled rather
 * than faked: projects do not nest (`ProjectTree` renders the one level the
 * schema holds), favourites have no column (per device, the shared `@/lib/favorites` store), and
 * "Mit mir geteilt" is not answerable (the tab is "Geteilt" — more than one
 * member — which is). Each is named in the page's own help.
 */

/**
 * The tabs.
 *
 * "Mit mir geteilt" is not among them: `folders.PostgresService.hydrate`
 * never fills `created_by`, so who *shared* a project with whom is not in the
 * answer. `members` is, so the tab says what is true — this project is not
 * yours alone.
 */
const TABS: { value: ProjectTab; label: string }[] = [
  { value: 'recent', label: 'Zuletzt' },
  { value: 'favorites', label: 'Favoriten' },
  { value: 'shared', label: 'Geteilt' },
]

export function ProjektePage() {
  const folders = useFolders()
  const templates = useTemplates()
  const { create, rename, remove, move } = useProjectMutations()
  const { favorites, toggle } = useFavorites()

  const [tab, setTab] = useState<ProjectTab>('recent')
  const [view, setView] = useState<'list' | 'grid'>('list')
  // A project card on the Übersicht sends its id here. The design has no
  // per-project screen — the artboards' folder cards are not even links — so
  // "open this project" means landing on this page with that one unfolded,
  // rather than on a list where nothing happened.
  const [params] = useSearchParams()
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => {
    const wanted = params.get('projekt')
    return wanted ? new Set([wanted]) : new Set()
  })
  const [helpOpen, setHelpOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [renaming, setRenaming] = useState<ProjectNode | null>(null)
  const [sharing, setSharing] = useState<ProjectNode | null>(null)
  const [deleting, setDeleting] = useState<ProjectNode | null>(null)
  const [moving, setMoving] = useState<Template | null>(null)

  const { query } = useShellSearch({ placeholder: 'Projekte durchsuchen' })
  useShellHelp({ onOpen: () => setHelpOpen(true) })

  const view$ = useMemo(
    () => buildProjects(folders.data ?? [], templates.data ?? []),
    [folders.data, templates.data],
  )

  const projects = useMemo(
    () => filterProjects(applyTab(sortByRecent(view$.projects), tab, favorites), query),
    [view$.projects, tab, favorites, query],
  )
  const unfiled = useMemo(() => filterTemplates(view$.unfiled, query), [view$.unfiled, query])

  function toggleExpanded(folderId: string) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const loading = folders.isPending || templates.isPending
  const failed = folders.isError || templates.isError
  // The gateway caps `page_size` at 100. A full page means there may be more,
  // and the search box only ever sees what was loaded — so say so instead of
  // letting a filter come up empty for a form that is simply not here.
  const truncated = (templates.data?.length ?? 0) >= TEMPLATE_PAGE_SIZE

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-6">
        <PageTitle className="flex-1">Projekte</PageTitle>
        <FxButton icon="create_new_folder" onClick={() => setCreateOpen(true)}>
          Neues Projekt
        </FxButton>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <FxSegmentedTabs
          tabWidth={220}
          aria-label="Projekte filtern"
          items={TABS}
          active={tab}
          onSelect={(value) => setTab(value as ProjectTab)}
          className="flex-1"
        />
        <FxViewToggle value={view} onChange={setView} />
      </div>

      {truncated && (
        <FxAlert tone="info">
          Es werden die ersten {TEMPLATE_PAGE_SIZE} Formulare geladen. Die Suche auf dieser Seite
          durchsucht nur diese.
        </FxAlert>
      )}

      {loading && (
        <FxPanel>
          <FxSpinner label="Projekte werden geladen …" />
        </FxPanel>
      )}

      {failed && !loading && (
        <FxAlert tone="danger" title="Die Projekte konnten nicht geladen werden.">
          <p className="m-0">Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.</p>
          <FxButton
            variant="outlined"
            className="mt-4"
            onClick={() => {
              void folders.refetch()
              void templates.refetch()
            }}
          >
            Erneut versuchen
          </FxButton>
        </FxAlert>
      )}

      {!loading && !failed && (
        <>
          {projects.length === 0 ? (
            <EmptyProjects
              query={query}
              tab={tab}
              hasAny={view$.projects.length > 0}
              onCreate={() => setCreateOpen(true)}
            />
          ) : view === 'list' ? (
            <FxPanel className="px-6 py-3">
              <ProjectTree
                projects={projects}
                favorites={favorites}
                expanded={expanded}
                onToggleExpanded={toggleExpanded}
                onToggleFavorite={toggle}
                onRename={setRenaming}
                onShare={setSharing}
                onDelete={setDeleting}
              />
            </FxPanel>
          ) : (
            <ProjectGrid
              projects={projects}
              favorites={favorites}
              onOpen={(project) => {
                // No `/projekte/:id` screen exists; the list is where a
                // project's contents can actually be shown.
                setView('list')
                setExpanded((current) => new Set(current).add(project.folder.id))
              }}
              onToggleFavorite={toggle}
              onRename={setRenaming}
              onShare={setSharing}
              onDelete={setDeleting}
            />
          )}

          {unfiled.length > 0 && <UnfiledPanel templates={unfiled} onMove={setMoving} />}
        </>
      )}

      <FolderNameDialog
        open={createOpen}
        mode="create"
        pending={create.isPending}
        error={create.isError ? 'Das Projekt konnte nicht angelegt werden.' : null}
        onSubmit={(name) => create.mutate(name, { onSuccess: () => setCreateOpen(false) })}
        onOpenChange={setCreateOpen}
      />

      <FolderNameDialog
        open={renaming != null}
        mode="rename"
        initialName={renaming?.folder.name ?? ''}
        pending={rename.isPending}
        error={rename.isError ? 'Der Name konnte nicht gespeichert werden.' : null}
        onSubmit={(name) =>
          renaming &&
          rename.mutate({ id: renaming.folder.id, name }, { onSuccess: () => setRenaming(null) })
        }
        onOpenChange={(open) => !open && setRenaming(null)}
      />

      <DeleteFolderDialog
        open={deleting != null}
        name={deleting?.folder.name ?? ''}
        templateCount={deleting?.templateCount ?? 0}
        pending={remove.isPending}
        error={remove.isError ? 'Das Projekt konnte nicht gelöscht werden.' : null}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.folder.id, { onSuccess: () => setDeleting(null) })
        }
        onOpenChange={(open) => !open && setDeleting(null)}
      />

      <ShareFolderDialog
        open={sharing != null}
        project={sharing}
        onOpenChange={(open) => !open && setSharing(null)}
      />

      <MoveTemplateDialog
        open={moving != null}
        templateTitle={moving?.title ?? ''}
        folders={folders.data ?? []}
        pending={move.isPending}
        error={move.isError ? 'Das Formular konnte nicht verschoben werden.' : null}
        onSubmit={(folderId) =>
          moving &&
          move.mutate(
            { templateId: moving.id, folderId },
            { onSuccess: () => setMoving(null) },
          )
        }
        onOpenChange={(open) => !open && setMoving(null)}
      />

      <ProjektDialog open={helpOpen} onOpenChange={setHelpOpen} title="Projekte">
        <ul className="m-0 flex list-disc flex-col gap-3 pl-5">
          <li>Projekte bündeln Ihre Musterformulare. Ein Formular liegt in höchstens einem Projekt.</li>
          <li>
            Projekte lassen sich derzeit nicht ineinander verschachteln. Unter einem Projekt stehen
            die Formulare, die darin liegen.
          </li>
          <li>
            Favoriten werden nur auf diesem Gerät gespeichert. Auf einem anderen Gerät oder in einem
            anderen Browser sehen Sie sie nicht.
          </li>
          <li>
            „Geteilt“ zeigt Projekte, auf die mehr als eine Person Zugriff hat — eigene und fremde.
          </li>
          <li>
            Wird ein Projekt gelöscht, bleiben seine Formulare erhalten und stehen danach unter
            „Ohne Projekt“.
          </li>
          <li>Die Suche durchsucht die geladenen Projekte und Formulare auf dieser Seite.</li>
        </ul>
      </ProjektDialog>
    </div>
  )
}

/** The three ways this list can be empty, told apart — "keine Treffer" on a
 *  first-run account is discouraging and wrong. */
function EmptyProjects({
  query,
  tab,
  hasAny,
  onCreate,
}: {
  query: string
  tab: ProjectTab
  hasAny: boolean
  onCreate: () => void
}) {
  if (query.trim()) {
    return (
      <FxPanel>
        <p className="m-0 text-body text-text-muted">
          Keine Projekte oder Formulare passen zu „{query.trim()}“.
        </p>
      </FxPanel>
    )
  }
  if (hasAny) {
    return (
      <FxPanel>
        <p className="m-0 text-body text-text-muted">
          {tab === 'favorites'
            ? 'Sie haben noch kein Projekt als Favorit gemerkt.'
            : 'Kein Projekt ist mit weiteren Personen geteilt.'}
        </p>
      </FxPanel>
    )
  }
  return (
    <FxPanel className="flex flex-col items-start gap-5">
      <FxIcon name="folder" size={64} filled className="text-primary-tonal" />
      <div>
        <p className="m-0 text-panel-title">Noch kein Projekt</p>
        <p className="m-0 mt-1 text-body text-text-muted">
          Legen Sie ein Projekt an, um Ihre Musterformulare zu bündeln.
        </p>
      </div>
      <FxButton icon="create_new_folder" onClick={onCreate}>
        Neues Projekt
      </FxButton>
    </FxPanel>
  )
}
